/* eslint-disable jsx-a11y/anchor-is-valid, max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Media from 'react-media';
import Collapsible from 'react-collapsible';
import { useRouter } from 'next/router';
import { Listbox, Transition } from '@headlessui/react';
import isEmpty from 'lodash.isempty';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import trackAction from 'utils/tracking';
import { formatAvailableDaysFromSchedules } from 'helpers/schedule';

import ActivityTickets from 'components/ActivityTickets';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import IconSvg from 'components/Core/IconSvg';
import OpeningTimes from 'components/OpeningTimes';

import GLOBAL_MEDIA_QUERIES from 'constants/Screen';

const getCollapseIcon = (icon) => (
  <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ md: true }}>
    {(matches) => {
      let size = 25;

      if (matches.xs || matches.sm) {
        size = 20;
      }

      return <IconSvg icon={icon} size={size} className="text-primary ml-auto" />;
    }}
  </Media>
);

const getActivityHeadOpened = (activityTitle) => {
  return (
    <button type="button" className="flex py-10 w-full h-full items-center justify-between focus:outline-none">
      <div className="h3 text-basic-700">{activityTitle}</div>
      {getCollapseIcon('arrow-up')}
    </button>
  );
};

const getActivityHeadClosed = (activityTitle) => {
  return (
    <button type="button" className="flex py-10 w-full h-full items-center justify-between focus:outline-none">
      <div className="h3 text-basic-700">{activityTitle}</div>
      {getCollapseIcon('arrow-down')}
    </button>
  );
};

const triggerEvent = (label, action) => {
  trackAction('opening-times-and-rates', label, action);
};

const renderActivitySchedules = (activity, hideOpeningHours = false) => {
  if (!isEmpty(activity)) {
    let activityTitle;
    let activityId;
    let price = null;
    let schedule = null;

    if (activity.pageType === 'CustomActivityPage') {
      ({ title: activityTitle, id: activityId, price = null, schedule = null } = activity);
    } else {
      ({
        activity: { title: activityTitle, id: activityId },
        price = null,
        schedule = null,
      } = activity);
    }

    return (
      (price || schedule) && (
        <Collapsible
          key={`activity-${activityId}`}
          trigger={getActivityHeadClosed(activityTitle)}
          triggerWhenOpen={getActivityHeadOpened(activityTitle)}
          onOpen={() => triggerEvent(activityTitle, 'open-activity-accordion')}
          onClose={() => triggerEvent(activityTitle, 'close-activity-accordion')}
          easing="cubic-bezier(0.25, 1, 0.5, 1)"
          className="w-full border-b border-basic-900"
          openedClassName="w-full border-b border-basic-900"
        >
          <div className="flex flex-wrap -mx-4">
            <div className="flex-full max-w-full lg:flex-2/3 lg:max-w-2/3 px-4">
              {price && <ActivityTickets tickets={price.tickets} />}
            </div>

            <div className="flex-full max-w-full lg:flex-1/3 lg:max-w-1/3 px-4 py-4">
              {schedule && (
                <OpeningTimes
                  activityTitle={activityTitle}
                  availableDays={formatAvailableDaysFromSchedules(schedule)}
                  hideOpeningHours={hideOpeningHours}
                  {...schedule}
                />
              )}
            </div>
          </div>
        </Collapsible>
      )
    );
  }
  return null;
};

const renderThemeItemDesktop = (theme, index, onClickCB, isActive = false) => {
  const { id: themeId, name: themeName } = theme;
  const activeClassName = 'text-basic-700 border-primary pointer-events-none focus:text-basic-700';
  return (
    <a
      key={`theme-${themeId}`}
      onClick={() => {
        triggerEvent(themeName, 'menu');
        onClickCB(theme);
      }}
      onKeyUp={(keyevent) => {
        if (keyevent.key === 'Enter') {
          triggerEvent(themeName, 'menu');
          onClickCB(theme);
        }
      }}
      role="link"
      tabIndex={index}
      className={`h2 pb-2 text-basic-500 mr-8 border-b-2 border-transparent hover:text-basic-600 focus:text-basic-600 focus:outline-none cursor-pointer ${
        isActive ? activeClassName : ''
      }`}
    >
      {themeName}
    </a>
  );
};

const ScheduleOverviewPage = ({
  page: { title, breadcrumbs, url: pageUrl },
  activities,
  themes,
  currentThemeId,
  unusualopeninghours: { hideOpeningHours },
}) => {
  const router = useRouter();
  const initialTheme = currentThemeId ? themes.find((t) => t.id === currentThemeId.toString()) : themes[0];
  const [selectedTheme, setSelectedTheme] = useState(initialTheme);

  const themeClickCB = async (targetTheme) => {
    const pathAs = `${pageUrl}?theme=${targetTheme.id}`;
    router
      .replace(
        {
          pathname: `/[...slug]?theme=${targetTheme.id}`,
          query: { slug: pageUrl, theme: `${targetTheme.id}` },
        },
        pathAs,
      )
      .then(() => {
        setSelectedTheme(targetTheme);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
      });
  };

  return (
    <>
      <div className="container mx-auto px-4 text-basic-700">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <h1 className="py-4 md:py-8 text-secondary">{title}</h1>
        <div data-test-id="themes" className="mb-8">
          <div className="hidden lg:flex">
            {themes.map((theme, index) =>
              renderThemeItemDesktop(theme, index, themeClickCB, theme.id === selectedTheme.id),
            )}
          </div>
          <div className="mt-8 w-full block lg:hidden">
            <Listbox
              value={selectedTheme}
              onChange={(theme) => {
                themeClickCB(theme);
              }}
            >
              {({ open }) => (
                <>
                  <Listbox.Button className="w-full focus:outline-none">
                    <div className="p-intro font-bold text-basic-700 w-full border-b-2 border-secondary flex items-center pb-4">
                      {selectedTheme.name} {open && getCollapseIcon('arrow-up')}
                      {!open && getCollapseIcon('arrow-down')}
                    </div>
                  </Listbox.Button>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="mt-2 focus:outline-none">
                      {themes.map((theme) => (
                        <Listbox.Option
                          key={`theme-dropdown-item-${theme.id}`}
                          value={theme}
                          className="focus:outline-none"
                        >
                          <div className="p-intro font-bold text-basic-700 w-full py-2 cursor-pointer">
                            {theme.name}
                          </div>
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
        </div>
        <div data-test-id="activities" className="u-block-margins">
          {activities.map((activity) => renderActivitySchedules(activity, hideOpeningHours))}
        </div>
      </div>
    </>
  );
};

ScheduleOverviewPage.getCustomInitialProps = async ({
  id,
  contentType,
  hostname,
  previewData,
  currentThemeId,
  unusualopeninghours,
}) => {
  const query = requireQueryByName('ScheduleOverviewPage');
  const token = previewData ? previewData.token : '';
  const { page, activities, themes } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
    themeId: currentThemeId,
  });

  return {
    page,
    extraPageProps: {
      activities,
      themes,
      currentThemeId,
      unusualopeninghours,
    },
  };
};

ScheduleOverviewPage.defaultProps = {
  activities: [],
  themes: [],
  unusualopeninghours: {
    hideOpeningHours: false,
  },
};

ScheduleOverviewPage.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        pageType: PropTypes.string.isRequired,
        activity: PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          theme: PropTypes.shape({
            icon: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
          }),
        }),
        schedule: PropTypes.shape({}),
        price: PropTypes.shape({
          tickets: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string.isRequired,
              price: PropTypes.string.isRequired,
              url: PropTypes.string,
            }),
          ),
        }),
      }),
      PropTypes.shape({
        pageType: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        theme: PropTypes.shape({
          icon: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        }),
        schedule: PropTypes.shape({}),
        price: PropTypes.shape({
          tickets: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string.isRequired,
              price: PropTypes.string.isRequired,
              url: PropTypes.string,
            }),
          ),
        }),
      }),
    ]),
  ),
  currentThemeId: PropTypes.number.isRequired,
  themes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
    }),
  ),
  unusualopeninghours: PropTypes.shape({
    hideOpeningHours: PropTypes.bool,
  }),
};

export default ScheduleOverviewPage;
